import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Banner {
  id: string;
  title: string;
  image_url: string;
  link_url?: string;
  banner_type: 'leaderboard' | 'medium_rectangle' | 'skyscraper';
  placement: 'homepage' | 'stories' | 'both';
  position_priority: number;
  is_active: boolean;
  start_date?: string;
  end_date?: string;
  click_count: number;
  impression_count: number;
}

export const useBanners = (placement: 'homepage' | 'stories') => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const { data, error } = await supabase
          .from('banners')
          .select('*')
          .eq('is_active', true)
          .in('placement', [placement, 'both'])
          .or(`start_date.is.null,start_date.lte.${new Date().toISOString()}`)
          .or(`end_date.is.null,end_date.gt.${new Date().toISOString()}`)
          .order('position_priority', { ascending: false })
          .order('created_at', { ascending: false });

        if (error) throw error;
        setBanners((data || []) as Banner[]);
      } catch (error) {
        console.error('Error fetching banners:', error);
        setBanners([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, [placement]);

  const trackImpression = async (bannerId: string) => {
    try {
      const { data: currentBanner } = await supabase
        .from('banners')
        .select('impression_count')
        .eq('id', bannerId)
        .single();
      
      if (currentBanner) {
        await supabase
          .from('banners')
          .update({ impression_count: currentBanner.impression_count + 1 })
          .eq('id', bannerId);
      }
    } catch (error) {
      console.error('Failed to track banner impression:', error);
    }
  };

  return { banners, loading, trackImpression };
};